import { useTranslation } from 'react-i18next';
import { useBdArtifactDefs, useBdDetail } from '../../../hooks/useBasicDesign.js';
import { useBdViewSettings } from '../../../hooks/useBdViewSettings.js';
import { ApiError } from '../../../lib/api/_request.js';
import { BdDiagrams, type BdDiagramKind } from './BdDiagrams.js';
import type { BdArtifact } from '../../../lib/api/basic-design.js';

export interface BdGraphPageProps {
  /** 基本設計のチケット */
  bdKey: string;
  /** 開いている図 */
  view: BdDiagramKind;
  /** 図を切り替える */
  onViewChange: (view: BdDiagramKind) => void;
  /** 選んだ札の節を基本設計のページで開くリンクの先 */
  sectionHref: (artifact: BdArtifact) => string;
}

/**
 * 基本設計の図だけのページ。基本設計のページから新しいタブで開き、図を画面いっぱいに読む。
 *
 * 列の表示と向きは基本設計のページと同じ個人の見え方を読み書きするので、どちらで変えても
 * もう一方に残る。札の節はこのタブでは書けないので、基本設計のページのその節へのリンクを出す。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 基本設計の全体図・ER 図・画面遷移図を画面いっぱいに描き、選んだ札の行と基本設計のページのその節へのリンクを出す
 * @graph-connects useBdDetail [calls] 基本設計の題名と保存済みの節の取得
 * @graph-connects useBdArtifactDefs [calls] 節の定義の取得
 * @graph-connects useBdViewSettings [calls] 個人の見え方
 * @graph-connects BdDiagrams [calls] 図の切り替えと動く図
 */
export function BdGraphPage({ bdKey, view, onViewChange, sectionHref }: BdGraphPageProps) {
  const { t } = useTranslation();
  const detailQuery = useBdDetail(bdKey);
  const defsQuery = useBdArtifactDefs();
  const { settings, update } = useBdViewSettings();
  const detail = detailQuery.data;
  const defs = defsQuery.data?.items;
  const error = detailQuery.error ?? defsQuery.error;

  return (
    // __root が AppShell で包まないので画面全体を図に使う。ページ自体はスクロールさせない
    // （図の中の移動とぶつかるため）
    <div className="flex h-dvh min-h-0 flex-col bg-[var(--color-bg-base)]">
      {error !== null ? (
        <p role="alert" className="p-3 text-sm text-[var(--color-text-secondary)]">
          {error instanceof ApiError ? error.message : t('bd.loadFailed')}
        </p>
      ) : detail === undefined || defs === undefined ? (
        <p className="p-3 text-sm text-[var(--color-text-muted)]">{t('bd.loading')}</p>
      ) : (
        <div className="min-h-0 flex-1">
          <BdDiagrams
            bdKey={bdKey}
            defs={defs}
            saved={detail.artifacts}
            settings={settings}
            onSettingsChange={update}
            sectionHref={sectionHref}
            view={view}
            onViewChange={onViewChange}
            fill
          />
        </div>
      )}
    </div>
  );
}
